import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";
import { InputAdornment } from "@mui/material";
import { SearchFilterList, SearchResults } from "..";
import {
  SCancelIcon,
  SFormControl,
  SGridResults,
  SGridContainer,
  SInput,
  SSearch,
  SCircularProgress,
} from "./styles";
import { useSearchRemoteAsset } from "../../../api";

interface SearchAssetPropsType {
  setSecondaryMessage: React.Dispatch<React.SetStateAction<boolean>>;
  secondaryMessage: boolean;
}

export const SearchAsset: React.FC<SearchAssetPropsType> = ({
  setSecondaryMessage,
  secondaryMessage,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState<JSON | string>("");
  const [selectedFilters, setSelectedFilters] = useState({
    funds: false,
    stocks: false,
    fixedIncome: false,
    pension: false,
  });
  const [isMatchedSearch, setIsMatchedSearch] = useState(false);
  const {
    loading: isSearchLoading,
    data: searchResults,
    fetch: searchAssets,
  } = useSearchRemoteAsset();

  const handleTextChange:
    | React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
    | undefined = (event) => {
    setSearchText(event.target.value);
  };

  const handleClear:
    | React.MouseEventHandler<SVGSVGElement>
    | undefined = () => {
    setSearchText("");
    setResults("");
  };

  const fetchSearchResults = useCallback(() => {
    if (searchText.length >= 3) {
      searchAssets(searchText);
    }
  }, [searchText, searchAssets]);

  useEffect(() => setResults(searchResults), [searchResults]);

  useDebounce(
    () => {
      fetchSearchResults();
    },
    300,
    [searchText]
  );

  const isAnyFilterSelected = useCallback((): boolean => {
    return (
      selectedFilters.funds === true ||
      selectedFilters.stocks === true ||
      selectedFilters.fixedIncome === true ||
      selectedFilters.pension === true
    );
  }, [selectedFilters]);

  const isSearchBoxEmpty = useCallback(
    (): boolean => searchText.length === 0,
    [searchText]
  );

  useEffect(() => {
    if (isAnyFilterSelected() || !isSearchBoxEmpty()) {
      setSecondaryMessage(true);
    } else {
      setSecondaryMessage(false);
    }
  }, [
    selectedFilters,
    setSecondaryMessage,
    isSearchBoxEmpty,
    isAnyFilterSelected,
  ]);

  const isInputEmpty = Boolean(searchText);
  const isSearchDone = Boolean(results);

  return (
    <SGridContainer>
      <SFormControl variant="standard">
        <SearchFilterList passSelectedToParent={setSelectedFilters} />
        <SInput
          id="searchBox"
          value={searchText}
          onChange={handleTextChange}
          startAdornment={
            <InputAdornment position="start">
              <SSearch />
            </InputAdornment>
          }
          placeholder={t("searchAsset.placeHolder")}
          disableUnderline
          endAdornment={
            <InputAdornment position="end">
              {(isInputEmpty || isSearchDone) && (
                <SCancelIcon onClick={handleClear} />
              )}
            </InputAdornment>
          }
        />
      </SFormControl>
      <SGridResults
        $isLoading={isSearchLoading}
        $isSearchDone={isMatchedSearch}
      >
        {isSearchLoading ? (
          <SCircularProgress />
        ) : (
          <SearchResults
            selectedFilters={selectedFilters}
            isSearching={isInputEmpty}
            data={results}
            secondaryMessage={secondaryMessage}
            setIsMatchedSearch={setIsMatchedSearch}
            isAnyFilterSelected={isAnyFilterSelected()}
          />
        )}
      </SGridResults>
    </SGridContainer>
  );
};
